import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { XIcon, BarcodeIcon, PrinterIcon, MinusIcon, PlusIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * PrintLabelsModal — Modal para imprimir etiquetas de bodega (Code128) de una recepción.
 *
 * Flujo:
 *   1. Al abrir, carga los items de la recepción vía GET /inventory/receipts/{ref}/items
 *   2. Muestra cada producto con inputs de "cantidad de etiquetas" (default = unidades recibidas, tope 50)
 *   3. Botón "Imprimir" → POST /inventory/receipts/{ref}/labels-pdf con los counts personalizados
 *      → abre el PDF en una nueva pestaña listo para imprimir
 *
 * Layout del PDF: A4 con 2 columnas × 5 filas = 10 etiquetas por hoja (5×5 cm cada una).
 */
export default function PrintLabelsModal({ referencia, onClose }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});           // { producto_id: number }
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/inventory/receipts/${encodeURIComponent(referencia)}/items`);
        if (cancelled) return;
        setItems(data.items || []);
        // Inicializar counts con el default sugerido por backend
        const init = {};
        (data.items || []).forEach((it) => { init[it.producto_id] = it.default_count || 1; });
        setCounts(init);
      } catch (e) {
        toast.error(apiErr(e));
        onClose?.();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [referencia, onClose]);

  const setCount = (pid, val) => {
    const n = Math.max(0, Math.min(500, parseInt(val || 0, 10)));
    setCounts((c) => ({ ...c, [pid]: n }));
  };

  const totalLabels = Object.values(counts).reduce((s, n) => s + (parseInt(n, 10) || 0), 0);
  const totalPages = Math.ceil(totalLabels / 10);

  const handlePrint = async () => {
    if (totalLabels === 0) {
      toast.error("Selecciona al menos 1 etiqueta");
      return;
    }
    setPrinting(true);
    try {
      // Pedimos el PDF como blob para abrirlo en una nueva pestaña
      const { data } = await api.post(
        `/inventory/receipts/${encodeURIComponent(referencia)}/labels-pdf`,
        { referencia, counts },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      // Liberar el objectURL después de un rato (dejamos tiempo a que el navegador abra la pestaña)
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      toast.success(`PDF generado: ${totalLabels} etiquetas (${totalPages} hoja${totalPages !== 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setPrinting(false);
    }
  };

  const vm = useModalForm({ onClose: onClose });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="print-labels-modal"
     {...vm.overlayProps}>
      <div
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-agro-green/15 flex items-center justify-center">
              <BarcodeIcon size={20} weight="duotone" className="text-agro-green" />
            </div>
            <div>
              <h2 className="font-heading text-lg font-semibold">Imprimir etiquetas de bodega</h2>
              <div className="text-xs text-text-muted font-mono">Recepción {referencia}</div>
            </div>
          </div>
          <button aria-label="Cerrar"
            onClick={vm.cerrar}
            data-testid="close-labels-modal"
            className="text-text-muted hover:text-text-primary transition"
          >
            <XIcon size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 border-2 border-agro-green border-t-transparent rounded-full animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-text-muted text-sm">Esta recepción no tiene productos.</div>
          ) : (
            <>
              <p className="text-xs text-text-muted mb-3">
                Ajusta cuántas etiquetas imprimir por producto. Cada etiqueta lleva código de barras Code128 con SKU y lote.
                <br />
                <span className="text-agro-green/80">Formato: A4 · 10 etiquetas por hoja (5 × 5 cm)</span>
              </p>
              <div className="border border-border/60 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="p-2.5 text-left">Producto</th>
                      <th className="p-2.5 text-left">Lote</th>
                      <th className="p-2.5 text-left">Vencimiento</th>
                      <th className="p-2.5 text-right">Recibido</th>
                      <th className="p-2.5 text-center w-40">Etiquetas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => (
                      <tr key={it.producto_id || idx} className="border-t border-border/40" data-testid={`label-row-${idx}`}>
                        <td className="p-2.5">
                          <div className="text-xs font-mono text-agro-green">{it.producto_codigo}</div>
                          <div className="text-xs text-text-secondary truncate max-w-xs">{it.producto_nombre || "—"}</div>
                        </td>
                        <td className="p-2.5 text-xs font-mono text-text-secondary">{it.lote || "—"}</td>
                        <td className="p-2.5 text-xs font-mono text-text-secondary">{(it.fecha_vencimiento || "").slice(0, 10) || "—"}</td>
                        <td className="p-2.5 text-right text-xs text-agro-yellow font-mono">{it.cantidad_recibida}</td>
                        <td className="p-2.5">
                          <div className="flex items-center justify-center gap-1">
                            <button aria-label="Quitar"
                              type="button"
                              onClick={() => setCount(it.producto_id, (counts[it.producto_id] || 0) - 1)}
                              className="w-7 h-7 rounded bg-sidebar hover:bg-sidebar-hover text-text-secondary flex items-center justify-center transition"
                              data-testid={`label-dec-${idx}`}
                            >
                              <MinusIcon size={12} />
                            </button>
                            <input
                              type="number"
                              min="0"
                              max="500"
                              value={counts[it.producto_id] || 0}
                              onChange={(e) => setCount(it.producto_id, e.target.value)}
                              className="w-14 bg-app border border-border rounded px-2 py-1 text-center text-sm text-text-primary focus:border-agro-green"
                              data-testid={`label-count-${idx}`}
                            />
                            <button aria-label="Agregar"
                              type="button"
                              onClick={() => setCount(it.producto_id, (counts[it.producto_id] || 0) + 1)}
                              className="w-7 h-7 rounded bg-sidebar hover:bg-sidebar-hover text-text-secondary flex items-center justify-center transition"
                              data-testid={`label-inc-${idx}`}
                            >
                              <PlusIcon size={12} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border/60 flex items-center justify-between bg-sidebar/30">
          <div className="text-xs text-text-secondary" data-testid="labels-total">
            <span className="text-agro-green font-semibold">{totalLabels}</span> etiquetas ·
            <span className="text-agro-yellow font-semibold ml-1">{totalPages}</span> hoja{totalPages !== 1 ? "s" : ""} A4
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={vm.cerrar}
              className="text-xs text-text-muted hover:text-text-primary px-3 py-2 transition"
              data-testid="cancel-labels-btn"
            >
              Cancelar
            </button>
            <button
              onClick={handlePrint}
              disabled={printing || loading || totalLabels === 0}
              data-testid="print-labels-btn"
              className="inline-flex items-center gap-2 bg-agro-green hover:bg-agro-green-hover disabled:opacity-40 text-black font-semibold rounded-md px-4 py-2 text-sm transition"
            >
              <PrinterIcon size={15} weight="duotone" />
              {printing ? "Generando…" : "Imprimir etiquetas"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
